import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PortalLayout from '@/components/layouts/PortalLayout';
import { 
  Truck, 
  Clock, 
  Shield, 
  CheckCircle2, 
  ArrowRight,
  Building2,
  PartyPopper,
  HardHat
} from 'lucide-react';

const features = [
  {
    icon: Truck,
    title: 'Reliable Delivery',
    description: 'On-time delivery and pickup for all your events and job sites.',
  },
  {
    icon: Clock,
    title: 'Flexible Scheduling',
    description: 'Daily, weekly, or monthly rentals to fit your project timeline.',
  },
  {
    icon: Shield,
    title: 'Clean & Maintained',
    description: 'Regular servicing ensures clean, hygienic units at all times.',
  },
];

const services = [
  {
    icon: HardHat,
    title: 'Construction Sites',
    description: 'Keep your crew productive with convenient on-site facilities. OSHA compliant solutions for projects of any size.',
  },
  {
    icon: PartyPopper,
    title: 'Special Events',
    description: 'From weddings to festivals, we provide clean, well-maintained units that keep your guests comfortable.',
  },
  {
    icon: Building2,
    title: 'Commercial Projects',
    description: 'Long-term rental solutions for commercial developments, renovations, and industrial facilities.',
  },
];

export default function LandingPage() {
  return (
    <PortalLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#194461] to-[#091D2E] text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="hero-title">
              Portable Toilet Rentals You Can Count On
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              Serving Johnson City, TN and the Tri-Cities area with professional, reliable portable sanitation solutions for construction sites, events, and more.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/portal/request-quote">
                <Button 
                  size="lg" 
                  className="bg-[#52B8E1] hover:bg-[#3AA0C9] text-[#091D2E] font-semibold px-8"
                  data-testid="hero-request-quote-btn"
                >
                  Request a Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:4235550100">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                  data-testid="hero-call-btn"
                >
                  Call (423) 555-0100
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Why Choose Tinkle Tent?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              With years of experience serving the Tri-Cities area, we deliver quality service every time.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-slate-200 hover:border-[#52B8E1] transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl bg-[#52B8E1]/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-[#52B8E1]" size={28} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From single-day events to multi-month construction projects, we have the right solution.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="bg-white border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 rounded-lg bg-[#194461] flex items-center justify-center mb-4">
                      <Icon className="text-white" size={24} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Unit Types Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Available Units</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We offer a variety of portable toilet options to meet your specific needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="aspect-square rounded-lg mb-4 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_portable-ops/artifacts/fpcbvev9_Standard%20Porta%20Potty.jpg" 
                    alt="Standard Porta Potty"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Standard Units</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Our most popular option. Durable, clean, and perfect for any job site or event.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Hand sanitizer included
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Regular servicing
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200 border-[#52B8E1] border-2 relative">
              <CardContent className="p-6">
                <div className="absolute top-0 right-0 bg-[#52B8E1] text-[#091D2E] text-xs font-semibold px-3 py-1 rounded-bl-lg z-10">
                  Popular
                </div>
                <div className="aspect-square rounded-lg mb-4 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_portable-ops/artifacts/314v3gcj_DSC09084-1-683x1024.jpg" 
                    alt="ADA Compliant Unit"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">ADA Compliant Units</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Spacious, wheelchair-accessible units that meet ADA requirements.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Extra-wide doorway
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Interior handrails
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="aspect-square rounded-lg mb-4 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_portable-ops/artifacts/pdardoi7_Gotugo_Products_12-Extended-Rental-Porta-Potty-with-Sink.jpg" 
                    alt="Extended Rental with Sink"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Premium Units with Sink</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Extended rental units with built-in hand washing sink for added convenience.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Built-in sink
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    Fresh water tank
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-[#194461] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-slate-300 text-lg mb-8">
            Get a free quote for your construction site, event, or commercial project today.
          </p>
          <Link to="/portal/request-quote">
            <Button 
              size="lg" 
              className="bg-[#52B8E1] hover:bg-[#3AA0C9] text-[#091D2E] font-semibold px-8"
              data-testid="cta-request-quote-btn"
            >
              Request a Free Quote
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </PortalLayout>
  );
}
